import { resolveDecValues } from "./decHelpers";

/**
 * Formules maîtresses « dimensions d'installation → cache »
 * (GlobalSettings.cache_dim_formulas).
 *
 * SYNC avec usipanel-backoffice/lib/evalCacheDimFormula.js — toute évolution
 * doit rester strictement alignée pour garantir le même rendu entre BO et
 * renderer.
 *
 * Variables exposées dans les formules :
 *   L, H, P1, P2       → dimensions d'installation (mm)
 *   DEC                → alias strict de DECi (rétrocompat)
 *   DECi               → décalage dérivé du TYPE D'INSTALLATION (hardcodé :
 *                        sol/sol-mur=-10, mur/mur-haut=+10)
 *   DECc               → décalage spécifique au PRODUIT (Products.dec, null
 *                        si non configuré). Utiliser `DECc ?? DECi` pour
 *                        retomber sur le fallback install si absent.
 *   BOTTOM, WALLMOUNT, HTOP, REAR → booléens (0/1)
 *   INSTALL_TYPE       → alias historique de INSTALL
 *   INSTALL            → type d'installation ('sol' | 'sol-mur' | 'mur' | 'mur-haut')
 *   P                  → alias largeur installation (= L)
 *   DEPTH              → max(P1, P2)
 *   Math / math        → Math standard + math.radians(deg) helper
 *   sizeX, sizeZ, rearHeight, sizeY → variables chaînées (ordre de calcul)
 *
 * Ordre de calcul (chaque variable est disponible dans les suivantes) :
 *   sizeX (profondeur) → sizeZ (haut.avant) → rearHeight (haut.arr.) → sizeY (largeur)
 */

export const EMPTY_MASTER_DIM_FORMULAS = {
  sizeX: '',
  sizeZ: '',
  rearHeight: '',
  sizeY: '',
};

/**
 * Prétraite une formule pour accepter les angles écrits en degrés ou radians,
 * comme dans l'éditeur de zones (FoldWorkspace.evaluateSimpleFormula). JS
 * travaille en radians, donc :
 *   - "2°" / "2deg" / "2 degrees" → "(2 * Math.PI / 180)"
 *   - "2rad" / "2 radians"        → "2" (déjà en radians)
 * Le nombre doit précéder le suffixe ; `math.radians(2)` reste inchangé.
 */
export function preprocessFormulaAngles(formula) {
  if (typeof formula !== 'string') return formula;
  return formula
    .replace(/(\d+(?:\.\d+)?)\s*(?:radians?|rad)\b/gi, '$1')
    .replace(/(\d+(?:\.\d+)?)\s*(?:degrees?|deg|°)/gi, '($1 * Math.PI / 180)');
}

export function normalizeMasterDimFormulas(raw) {
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }
  if (!parsed || typeof parsed !== 'object') {
    return { ...EMPTY_MASTER_DIM_FORMULAS };
  }
  return {
    sizeX: parsed.sizeX || '',
    sizeZ: parsed.sizeZ || '',
    rearHeight: parsed.rearHeight || '',
    sizeY: parsed.sizeY || '',
  };
}

/**
 * Minimums DURS des dimensions du cache (onglet 2 de l'étape 4) — seuils FIXES.
 *
 * Convention renderer : sizeX = largeur, sizeZ = profondeur,
 * sizeY = hauteur ARRIÈRE, frontHeight = hauteur AVANT (esclave).
 * Si l'avant calculé (arrière − pente 2°) passe sous minFrontHeight, l'arrière
 * est remontée pour ramener l'avant au minimum.
 */
export const CACHE_DIM_HARD_MINIMUMS = {
  minWidth: 300, // largeur
  minDepth: 250, // profondeur
  minFrontHeight: 300, // hauteur AVANT (l'arrière est ajustée pour la respecter via la pente)
};

/**
 * Fusionne une config (GlobalSettings.cache_dim_minimums) avec les valeurs par
 * défaut, en ne gardant que les 3 clés numériques valides. Tolère un JSON string.
 */
export function normalizeCacheDimMinimums(raw) {
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }
  if (!parsed || typeof parsed !== "object") {
    return { ...CACHE_DIM_HARD_MINIMUMS };
  }
  const merged = { ...CACHE_DIM_HARD_MINIMUMS };
  for (const key of Object.keys(CACHE_DIM_HARD_MINIMUMS)) {
    const value = parsed[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      merged[key] = value;
    }
  }
  return merged;
}

/** Ramène une valeur à son minimum si elle est inférieure (clamp dur). */
export function clampCacheDimension(value, minimum) {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return minimum;
  return Math.max(numeric, minimum);
}

const SIN2_DEGREES = Math.sin((2 * Math.PI) / 180);

/** Pente 2° en mm pour une profondeur de cache donnée (renderer sizeZ). */
export function cacheSlopeMm(depthMm) {
  return SIN2_DEGREES * (Number(depthMm) - 15);
}

/**
 * Applique le seuil sur la hauteur AVANT : si arrière − pente < minFront,
 * remonte l'arrière à ⌈minFront + pente⌉ et fixe l'avant à minFront.
 * Aligné sur ConfiguratorForm.recalculateDimensions (production).
 */
export function applyMinFrontToRearHeight(
  rearMm,
  depthMm,
  minFrontHeight = CACHE_DIM_HARD_MINIMUMS.minFrontHeight
) {
  const minFront = Number(minFrontHeight) || 0;
  const slope = cacheSlopeMm(depthMm);
  let rearY = Number(rearMm);
  let frontZ = Math.floor(rearY - slope);
  if (minFront > 0 && frontZ < minFront) {
    frontZ = minFront;
    rearY = Math.ceil(minFront + slope);
  }
  return { rearY, frontZ };
}

/**
 * Calcule les dimensions cache (renderer) à partir des cotes d'installation.
 * Même logique que ConfiguratorForm.recalculateDimensions (mode auto).
 */
export function computeCacheDimensionsFromInstall({
  sizeL,
  sizeH,
  sizeP1,
  sizeP2,
  type = "",
  wallmount = true,
  bottom = false,
  rear = false,
  productDec = null,
  dimFormulas = null,
  cacheMinimums = CACHE_DIM_HARD_MINIMUMS,
}) {
  const sin2Degrees = Math.sin((2 * Math.PI) / 180);
  const { DEC, DECi, DECc } = resolveDecValues(type, productDec);
  const formulaVars = {
    L: sizeL,
    H: sizeH,
    P1: sizeP1,
    P2: sizeP2,
    DEC,
    DECi,
    DECc,
    BOTTOM: !!bottom,
    WALLMOUNT: !!wallmount,
    HTOP: false,
    REAR: !!rear,
    INSTALL_TYPE: type,
    INSTALL: type,
  };

  let depthX = evalCacheDimFormula(dimFormulas?.sizeX, formulaVars);
  if (depthX === null) {
    depthX = sizeP1 + 80 >= sizeP2 + 30 ? sizeP1 + 80 : sizeP2 + 30;
    if (depthX < 250) depthX = 250;
  }

  const isWallmount = !!wallmount;
  const hasBottom = !!bottom;
  let frontZ = evalCacheDimFormula(dimFormulas?.sizeZ, formulaVars, { sizeX: depthX });
  if (frontZ === null) {
    let hFactor = 30;
    if (isWallmount && hasBottom) hFactor = 70;
    else if (isWallmount) hFactor = 40;
    frontZ = Math.max(sizeH + hFactor, 250);
  }

  const minFront = cacheMinimums?.minFrontHeight ?? 0;
  let rearY = Math.ceil(frontZ + sin2Degrees * (depthX - 15));
  const heightAdjusted = applyMinFrontToRearHeight(rearY, depthX, minFront);
  rearY = heightAdjusted.rearY;
  frontZ = heightAdjusted.frontZ;

  const rearHeightForFormula =
    evalCacheDimFormula(dimFormulas?.rearHeight, formulaVars, {
      sizeX: depthX,
      sizeZ: frontZ,
    }) ?? rearY;

  let widthY = evalCacheDimFormula(dimFormulas?.sizeY, formulaVars, {
    sizeX: depthX,
    sizeZ: frontZ,
    rearHeight: rearHeightForFormula,
  });
  if (widthY === null) {
    widthY = sizeL + 90;
    if (sizeL < 310) widthY = 400;
  }

  return {
    sizeX: widthY,
    sizeY: rearY,
    sizeZ: depthX,
    frontHeight: frontZ,
    noClipX: widthY - sizeL,
    noClipY: frontZ - sizeH,
    noClipZP1: depthX - sizeP1,
    noClipZP2: depthX - sizeP2,
  };
}

export function evalCacheDimFormula(formula, vars, extraVars = {}) {
  if (!formula || typeof formula !== 'string' || formula.trim() === '') return null;
  try {
    const safeFormula = preprocessFormulaAngles(formula);
    const fn = new Function(
      'L',
      'H',
      'P1',
      'P2',
      'DEC',
      'DECi',
      'DECc',
      'BOTTOM',
      'WALLMOUNT',
      'HTOP',
      'REAR',
      'INSTALL_TYPE',
      'INSTALL',
      'sizeX',
      'sizeZ',
      'rearHeight',
      'sizeY',
      'Math',
      `
      const DEPTH = Math.max(P1, P2);
      // Alias historique demandé côté métier : P représente la largeur d'installation.
      // Pour la profondeur d'installation, utiliser DEPTH (max de P1/P2).
      const P = L;
      // Les méthodes de Math ne sont pas énumérables : Object.assign({}, Math) ne copie pas max/cos/etc.
      const math = new Proxy(Math, {
        get(target, prop) {
          if (prop === 'radians') return (deg) => (deg * Math.PI) / 180;
          return Reflect.get(target, prop);
        },
      });
      return (${safeFormula});
      `
    );
    // DEC sans valeur explicite retombe sur DECi (alias de rétrocompat).
    const DEC_value = vars.DEC ?? vars.DECi ?? null;
    const DECi_value = vars.DECi ?? vars.DEC ?? null;
    const DECc_value = vars.DECc ?? null;
    const result = fn(
      vars.L,
      vars.H,
      vars.P1,
      vars.P2,
      DEC_value,
      DECi_value,
      DECc_value,
      !!vars.BOTTOM,
      !!vars.WALLMOUNT,
      !!vars.HTOP,
      !!vars.REAR,
      vars.INSTALL_TYPE ?? null,
      vars.INSTALL ?? null,
      extraVars.sizeX,
      extraVars.sizeZ,
      extraVars.rearHeight,
      extraVars.sizeY,
      Math
    );
    if (typeof result === 'number' && !isNaN(result) && isFinite(result)) return result;
    return null;
  } catch {
    return null;
  }
}
